import * as React from 'react';
import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { TemplateConfigurationWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { TEMPLATE_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { SimplePagination } from '@klareospace/pharos-lib-web-utils';
import { DataTable, useDataTablePagination } from '@klareospace/pharos-lib-web-utils';

import { useUpdateShipperStatus } from '../hooks/use-update-shipper-status';
import type { ShipperRow } from '../hooks/use-shippers-table-columns';
import { toShipperRow, useShippersTableColumns } from '../hooks/use-shippers-table-columns';
import { InstallShipperDrawer } from './install-shipper-drawer';
import { ShipperUninstallDialog } from './shipper-uninstall-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ShippersTableProps {
  configurations: TemplateConfigurationWithResolvedRefs[];
  isLoading: boolean;
  pagination: SimplePagination;
}

interface EditingRow {
  configId: string;
  templateId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ShippersTable({ configurations, isLoading, pagination }: ShippersTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  const [editingRow, setEditingRow] = useState<EditingRow | null>(null);
  const [pendingUninstallRow, setPendingUninstallRow] = useState<ShipperRow | null>(null);

  const statusMutation = useUpdateShipperStatus();

  const rows = useMemo<ShipperRow[]>(() => configurations.map((c) => toShipperRow(c)), [configurations]);

  /**
   * Edit action — opens the install drawer in edit mode for the row's template.
   * Guards against an unresolvable template: if `isTemplateResolved` is false on
   * the row, the backend could not locate the template (signalled by `name` falling
   * back to the raw `templateId`) — show an error toast and abort.
   */
  function handleEditRow(row: ShipperRow): void {
    if (!row.isTemplateResolved) {
      toast.error(t('shippers.toast.edit.unavailable'));
      return;
    }
    setEditingRow({ configId: row.id, templateId: row.templateId });
  }

  function handlePauseRow(row: ShipperRow): void {
    if (statusMutation.isPending) return;
    statusMutation.mutate({ id: row.id, status: TEMPLATE_CONFIGURATION_STATUS.INACTIVE });
  }

  function handleResumeRow(row: ShipperRow): void {
    if (statusMutation.isPending) return;
    statusMutation.mutate({ id: row.id, status: TEMPLATE_CONFIGURATION_STATUS.ACTIVE });
  }

  function handleUninstallRow(row: ShipperRow): void {
    setPendingUninstallRow(row);
  }

  function handleUninstallDialogOpenChange(open: boolean): void {
    if (!open) setPendingUninstallRow(null);
  }

  function handleUninstallConfirm(): void {
    if (!pendingUninstallRow || statusMutation.isPending) return;
    statusMutation.mutate(
      { id: pendingUninstallRow.id, status: TEMPLATE_CONFIGURATION_STATUS.ARCHIVED },
      { onSuccess: () => setPendingUninstallRow(null) },
    );
  }

  const columns = useShippersTableColumns({
    onEditRow: handleEditRow,
    onPauseRow: handlePauseRow,
    onResumeRow: handleResumeRow,
    onUninstallRow: handleUninstallRow,
  });

  const tablePagination = useDataTablePagination({
    labels: {
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current, total): string => tCommon('pagination.pageLabel', { current, total }),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (start, end, total): string => t('shippers.table.showing', { end, start, total }),
    },
    pagination,
  });

  return (
    <>
      <DataTable<ShipperRow>
        ariaLabel={t('shippers.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('shippers.table.empty')}
        isLoading={isLoading}
        pagination={tablePagination}
        rowKey={(row): string => row.id}
        rows={rows}
      />
      <InstallShipperDrawer
        configId={editingRow?.configId ?? null}
        mode="edit"
        onClose={(): void => setEditingRow(null)}
        open={editingRow !== null}
        templateId={editingRow?.templateId ?? null}
      />
      <ShipperUninstallDialog
        isConfirmPending={statusMutation.isPending}
        name={pendingUninstallRow?.name ?? null}
        onConfirm={handleUninstallConfirm}
        onOpenChange={handleUninstallDialogOpenChange}
        open={pendingUninstallRow !== null}
      />
    </>
  );
}
